import { captureException } from '@flex-packages/mf-sentry';
import { serialize } from 'cookie';
import Cookies from 'universal-cookie';
import { createCookieChangedBus } from '../bus/index.mjs';
import { COOKIE_KEY, DEFAULT_COOKIE_OPTIONS, isServer, isTest } from '../constants.mjs';
import { uniDirectionSerializer } from '../serializers/json.mjs';

const isEqual = (a, b) => typeof a === typeof b && uniDirectionSerializer(a) === uniDirectionSerializer(b);
const shouldIgnoreSingleton = isServer || isTest;
class CookieManager {
  /** @deprecated Use getInstance. */
  constructor(params) {
    this.params = params;
    //
    this.cookies = new Cookies(this.params.cookieString);
    // ⬇️ I NOTIFY others (react)
    this.outgoingFn = /* @__PURE__ */ new Set();
    // ⬇️ I LISTEN to others (cookie)
    this.incomingFn = /* @__PURE__ */ new Set();
    this.lastChange = { key: null, time: 0 };
    // initial state
    // source of truth
    this.state = Object.values(COOKIE_KEY).reduce((rec, key) => {
      rec[key] = this.cookies.get(key);
      return rec;
    }, {});
    this.dead = false;
    this.setState = (key, value, skipEmit = false) => {
      if (isEqual(this.state[key], value)) {
        return;
      }
      this.state = { ...this.state, [key]: value };
      if (!skipEmit) {
        this.cookieChangedBus.emit({ key, time: Date.now() });
      }
      this.outgoingFn.forEach((listener) => listener());
    };
    this.getCookie = (key, skipEmit) => {
      const value = this.cookies.get(key);
      this.setState(key, value, skipEmit);
      return this.state[key];
    };
    this.invalidateState = (key) => {
      this.getCookie(key, true);
    };
    this.cookieChangedBus = createCookieChangedBus({
      getPayload: () => this.lastChange,
      onMessage: (message) => {
        this.lastChange = message;
        const { key } = message;
        if (!key) {
          return;
        }
        this.invalidateState(key);
      }
    });
    this.mergeOptions = (override) => ({
      ...DEFAULT_COOKIE_OPTIONS,
      secure: this.params.secure,
      ...override
    });
    this.get = (key, skipEmit = false) => this.getCookie(key, skipEmit);
    this.set = (key, value, options) => {
      if (this.dead) {
        console.warn("CookieManager is dead", key, value);
      }
      this.cookies.set(key, value, this.mergeOptions(options));
      this.setState(key, value);
    };
    this.remove = (key, options) => {
      this.cookies.remove(key, this.mergeOptions(options));
      this.setState(key, void 0);
    };
    this.getSnapshot = () => this.state;
    this.subscribe = (listener) => {
      this.outgoingFn.add(listener);
      return () => void this.outgoingFn.delete(listener);
    };
    /** Only for debug :) */
    this.peek = () => this.state;
    // destroyer for server
    this.destroy = () => {
      const { incomingFn, outgoingFn } = this;
      this.dead = true;
      incomingFn.forEach((disposer) => disposer());
      incomingFn.clear();
      outgoingFn.clear();
    };
    const { res } = params;
    this.incomingFn.add(this.cookieChangedBus.destroy);
    if (res) {
      const handler = ({ name, value, options }) => {
        if (res.headersSent) {
          return;
        }
        try {
          const serializedCookie = serialize(
            name,
            value,
            this.mergeOptions(options)
          );
          res.appendHeader("Set-Cookie", serializedCookie);
        } catch (e) {
          captureException(e);
        }
      };
      this.cookies.addChangeListener(handler);
      this.incomingFn.add(() => this.cookies.removeChangeListener(handler));
    }
  }
  static getInstance(...params) {
    if (shouldIgnoreSingleton) {
      return new CookieManager(...params);
    }
    const existing = CookieManager.instance;
    if (existing && !existing.dead) {
      return existing;
    }
    return CookieManager.instance ||= new CookieManager(...params);
  }
}

export { CookieManager };
//# sourceMappingURL=class.mjs.map
